/**
 * Time utilities (constitution §4.7). The only place the portal timezone is named.
 * Storage and API transport are UTC ISO strings; display and form input are Los Angeles wall time.
 * Uses Intl only, no external library.
 */
export const TZ = 'America/Los_Angeles';
/** Windows zone id Microsoft Graph expects for Pacific time. */
export const GRAPH_TZ = 'Pacific Standard Time';
const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const partsFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});
/** Wall-clock parts of a UTC instant in the portal timezone. */
export function wallParts(utc) {
    const get = (type) => {
        const part = partsFormatter.formatToParts(utc).find((p) => p.type === type);
        return Number(part?.value ?? '0');
    };
    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
        second: get('second'),
    };
}
function asUtcMs(p) {
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}
/** Minutes to add to UTC to get the portal wall clock (-420 in PDT, -480 in PST). */
export function utcOffsetMinutes(utc) {
    return Math.round((asUtcMs(wallParts(utc)) - utc.getTime()) / 60_000);
}
function pad(n) {
    return String(n).padStart(2, '0');
}
export class InvalidLocalTimeError extends Error {
    name = 'InvalidLocalTimeError';
}
/**
 * Converts a form input like "2026-11-01T01:30" (Los Angeles wall time) to a UTC Date.
 * Ambiguous times at DST end resolve to the first occurrence (daylight offset).
 * Non-existent times in the DST-start gap throw InvalidLocalTimeError.
 */
export function toUtc(localInput) {
    const m = LOCAL_INPUT.exec(localInput);
    if (!m)
        throw new InvalidLocalTimeError(`expected YYYY-MM-DDTHH:mm, got "${localInput}"`);
    const wanted = {
        year: Number(m[1]),
        month: Number(m[2]),
        day: Number(m[3]),
        hour: Number(m[4]),
        minute: Number(m[5]),
        second: Number(m[6] ?? '0'),
    };
    if (Number.isNaN(asUtcMs(wanted)) || wanted.month < 1 || wanted.month > 12) {
        throw new InvalidLocalTimeError(`invalid date "${localInput}"`);
    }
    const wantedMs = asUtcMs(wanted);
    // Try both Pacific offsets; keep candidates whose wall time round-trips exactly.
    const candidates = [7, 8]
        .map((offsetHours) => new Date(wantedMs + offsetHours * 3_600_000))
        .filter((d) => asUtcMs(wallParts(d)) === wantedMs);
    const first = candidates[0];
    if (!first) {
        throw new InvalidLocalTimeError(`"${localInput}" does not exist in ${TZ} (DST gap)`);
    }
    return first;
}
/** "YYYY-MM-DD HH:mm" in the portal timezone, for tables and emails. */
export function formatLocal(utc) {
    const p = wallParts(typeof utc === 'string' ? new Date(utc) : utc);
    return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
}
/** "YYYY-MM-DDTHH:mm:ss" wall time for Graph event start/end paired with GRAPH_TZ. */
export function toLocalWall(utc) {
    const p = wallParts(typeof utc === 'string' ? new Date(utc) : utc);
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`;
}
/** "YYYY-MM-DD" of the instant in the portal timezone (reminder day boundaries). */
export function localDate(utc) {
    return formatLocal(utc).slice(0, 10);
}
/** Day of week in the portal timezone, 0 = Sunday. */
export function localWeekday(utc) {
    const d = typeof utc === 'string' ? new Date(utc) : utc;
    const name = new Intl.DateTimeFormat('en-US', { timeZone: TZ, weekday: 'short' }).format(d);
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}
export function isWeekend(utc) {
    const wd = localWeekday(utc);
    return wd === 0 || wd === 6;
}
export function addMinutes(utc, minutes) {
    return new Date(utc.getTime() + minutes * 60_000);
}
//# sourceMappingURL=time.js.map